import { ChevronDownIcon } from '@radix-ui/react-icons'
import { useNavigate } from '@tanstack/react-router'
import { useKestrel, useKestrelViewerContext } from 'features/kestrelViewer'
import { useInspectionAreaForms } from 'features/kestrelViewer/hooks/useInspectionAreaForms'
import {
  InputGroup,
  SectionHeader,
} from 'features/kestrelViewer/ui/popupMenu/shared/MarkupPopupContainer'
import React, { useState } from 'react'
import styled from 'styled-components'

const CollapsibleHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  padding: 0.25rem 0;
  user-select: none;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
  }
`

const ChevronIcon = styled(ChevronDownIcon)<{ $isExpanded: boolean }>`
  width: 1rem;
  height: 1rem;
  transition: transform 0.2s ease;
  transform: ${(props) =>
    props.$isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'};
`

const CollapsibleContent = styled.div<{ $isExpanded: boolean }>`
  max-height: ${(props) => (props.$isExpanded ? '300px' : '0')};
  overflow: hidden;
  transition: max-height 0.3s ease-in-out;
`

const FormList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.25rem;
`

const FormItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.25rem;
  cursor: pointer;
  user-select: none;
  transition: all 0.2s ease;
  border-radius: 4px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.875rem;

  &:hover {
    background: rgba(59, 130, 246, 0.15);
    color: rgba(96, 165, 250, 0.95);
  }
`

const EmptyState = styled.div`
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.5);
  font-style: italic;
  padding: 0.5rem 0;
`

interface FormsDropdownProps {
  inspectionAreaId: string
}

export const FormsDropdown: React.FC<FormsDropdownProps> = ({
  inspectionAreaId,
}) => {
  const { kestrelModel } = useKestrel()
  const { setSelectedMarkup } = useKestrelViewerContext()
  const navigate = useNavigate()
  const [isExpanded, setIsExpanded] = useState(false)

  const { forms, isLoading } = useInspectionAreaForms(
    kestrelModel?.id,
    inspectionAreaId
  )

  const handleFormClick = (formId: string) => {
    setSelectedMarkup(undefined)
    navigate({
      to: '/kestrel/forms/$inspectionType/$formName',
      params: { inspectionType: 'aerospace', formName: '781a' },
      search: {
        kestrelModelId: kestrelModel?.id,
        selectedFormId: formId,
      },
    })
  }

  if (isLoading) {
    return <EmptyState>Loading...</EmptyState>
  }

  if (forms.length === 0) {
    return null
  }

  return (
    <InputGroup>
      <CollapsibleHeader onClick={() => setIsExpanded(!isExpanded)}>
        <SectionHeader>781A Forms ({forms.length})</SectionHeader>
        <ChevronIcon $isExpanded={isExpanded} />
      </CollapsibleHeader>
      <CollapsibleContent $isExpanded={isExpanded}>
        <FormList>
          {forms.map((form) => (
            <FormItem
              key={form.formId}
              onClick={() => handleFormClick(form.formId)}>
              {form.formName}
            </FormItem>
          ))}
        </FormList>
      </CollapsibleContent>
    </InputGroup>
  )
}
